import { Skeleton } from 'components/Skeleton/Skeleton'
import { ReactComponent as RightArrow } from 'assets/icons/blockyRightArrow16.svg'
import cx from 'classnames'
import Avatar from 'common/Avatar/Avatar'
import { CommentInteractionButton } from 'common/InteractionButton'
import { TaskInteractionButton } from 'common/InteractionButton/components/TaskInteractionButton/TaskInteractionButton'
import { updateCardFrameViewSize } from 'common/Prose/Commands/updateCardFrameViewSize'
import { IReactNodeViewContext } from 'common/Prose/nodeViews/ReactNodeView/ReactNodeViewContext'
import { MentionSubject } from 'common/Prose/utils/smartLinks/types'
import { ArticlePreview } from 'components/ArticlePreview/ArticlePreview'
import { Button } from 'components/Button/Button'
import { UserProfileCardContainer } from 'components/UserProfileCard/UserProfileCardContainer'
import { useRequestReadAccess } from 'hooks/queries/articles/useRequestReadAccess'
import { useMemberProfile } from 'hooks/useMemberProfile'
import { clamp } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { ResizableBox, ResizeHandle } from 'react-resizable'
import { useRootStore } from 'stores/RootStore'
import { resizeHandleToCursor } from 'utils/resizable'
import { commentAriaLabel, taskAriaLabel } from '../CardViewComponent/utils/textUtils'
import Styles from './FrameViewComponent.module.scss'

type FrameViewProps = {
  nodeViewContext: IReactNodeViewContext
  comments: Array<string>
  tasks: Array<string>
  mentionSubject: MentionSubject
  subtitle: string
  selected: boolean
  commentSelected?: boolean
  workspaceId?: number
  memberId?: number
  showInlineMenu: (setShow: boolean) => void
  loading?: boolean
  isError?: boolean
}

export const FrameViewComponent = observer((props: FrameViewProps) => {
  const { nodeViewContext, selected, commentSelected = false, showInlineMenu, loading, isError } = props
  const { view, node, getPos, inCommentMode } = nodeViewContext
  const { attrs } = node
  const isUser = attrs.mentionType === 'User'
  const isArticle = attrs.mentionType === 'Article'
  const MIN_WIDTH = 233
  const MIN_HEIGHT = isUser ? 300 : 200
  const MAX_WIDTH = 700
  const MAX_HEIGHT = 4000
  const [resizeHandle, setResizeHandle] = useState<null | ResizeHandle>(null)
  const [boxHeight] = useState(attrs.height)
  const [boxWidth] = useState(clamp(attrs.width, MIN_WIDTH, MAX_WIDTH))

  if (!isError && loading && isArticle) return <FrameViewSkeleton selected={selected} />

  const getResizeHandle = (position: string) => {
    return (
      <span
        className={cx(Styles.resizeHandle, Styles[`resizeHandle-${position}`], {
          [Styles.hide]: !selected,
        })}
        data-test-id={`embed-resize-handle-${position}`}
      />
    )
  }

  return (
    <div
      data-test-id="frameViewComponent"
      className={cx(Styles.frameView, {
        [Styles.frameSelected]: selected,
        [Styles.commentSelected]: commentSelected,
        [Styles.restricted]: isError,
      })}
    >
      {!view.editable ? (
        <div className={Styles.readOnlyFrameContainer} style={{ height: `${boxHeight}px`, width: `${boxWidth}px` }}>
          <RenderFrame {...props} />
          {inCommentMode && <InteractionButtons {...props} />}
        </div>
      ) : (
        <ResizableBox
          className={Styles.resizeContainer}
          height={boxHeight}
          width={boxWidth}
          lockAspectRatio={false}
          onResizeStop={(_event, { size: { width, height } }) => {
            setResizeHandle(null)
            showInlineMenu(true)
            updateCardFrameViewSize({ width, height, nodePos: getPos() })(view.state, view.dispatch)
          }}
          onResizeStart={(_event, { handle }) => {
            _event.preventDefault()
            showInlineMenu(false)
            setResizeHandle(handle)
          }}
          minConstraints={[MIN_WIDTH, MIN_HEIGHT]}
          maxConstraints={[MAX_WIDTH, MAX_HEIGHT]}
          resizeHandles={['n', 's', 'e', 'w', 'nw', 'ne', 'se', 'sw']}
          handle={getResizeHandle}
        >
          {resizeHandle && (
            <style>{`
                  body {
                    cursor: ${resizeHandleToCursor(resizeHandle)};
                  }
                `}</style>
          )}
          <RenderFrame {...props} />
          <InteractionButtons {...props} />
        </ResizableBox>
      )}
    </div>
  )
})

const RenderFrame = ({
  isError,
  mentionSubject,
  subtitle,
  nodeViewContext,
  workspaceId,
  memberId,
}: Pick<
  FrameViewProps,
  'isError' | 'mentionSubject' | 'subtitle' | 'nodeViewContext' | 'workspaceId' | 'memberId'
>) => {
  const { node } = nodeViewContext
  const { attrs } = node
  const isUser = attrs.mentionType === 'User'
  const isArticle = attrs.mentionType === 'Article'
  const { profile, error } = useMemberProfile(workspaceId, memberId)
  const [requestReadAccess, requestingAccess] = useRequestReadAccess()

  return (
    <div className={Styles.frame}>
      {isArticle && (
        <div className={Styles.content}>
          <div className={Styles.document}>
            {isError ? (
              <>
                <Avatar className={Styles.restrictedAvatar} size={93} {...mentionSubject.avatar} />
                <DocumentSkeleton />
              </>
            ) : (
              <ArticlePreview articleId={attrs.key} />
            )}
          </div>
        </div>
      )}
      {isUser && <UserProfileCardContainer reduced={false} profile={profile} error={error} />}
      {isArticle && (
        <div className={Styles.metadata}>
          <div className={Styles.metadataLeft}>
            <Avatar className={Styles.avatar} size={32} {...mentionSubject.avatar} />
            <div className={Styles.titleContainer}>
              <p className={Styles.title}>{mentionSubject.title}</p>
              <p className={Styles.subtitle}>{subtitle}</p>
            </div>
          </div>
          {isError && (
            <Button
              isDisabled={requestingAccess}
              variant="secondary"
              endIcon={RightArrow}
              onPress={() => requestReadAccess(attrs.key)}
            >
              Request Access
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

const InteractionButtons = ({
  nodeViewContext,
  comments,
  tasks,
}: Pick<FrameViewProps, 'nodeViewContext' | 'comments' | 'tasks'>) => {
  const { view } = nodeViewContext
  const rootStore = useRootStore()
  const docStore = rootStore.docStore

  if (!docStore) return null

  return comments.length > 0 || tasks.length > 0 ? (
    <div className={Styles.interactionButtons}>
      {comments.length > 0 && (
        <CommentInteractionButton aria-label={commentAriaLabel} uuids={comments} docStore={docStore} />
      )}
      {view.editable && tasks.length > 0 && (
        <TaskInteractionButton aria-label={taskAriaLabel} uuids={tasks} docStore={docStore} />
      )}
    </div>
  ) : null
}

const FrameViewSkeleton = ({ selected }: { selected: boolean }) => {
  return (
    <div
      className={cx(Styles.frameView, Styles.loading, {
        [Styles.frameSelected]: selected,
      })}
    >
      <div className={Styles.frame}>
        <div className={Styles.content}>
          <div className={Styles.document}>
            <DocumentSkeleton />
          </div>
        </div>
        <div className={Styles.metadata}>
          <div className={Styles.metadataLeft}>
            <Avatar className={Styles.avatar} size={32} />
            <div className={Styles.titleContainer}>
              <Skeleton className={Styles.titleSkeleton} paragraph={false} title={false} />
              <Skeleton className={Styles.subtitleSkeleton} paragraph={false} title={false} />
            </div>
          </div>
          <Skeleton className={Styles.buttonSkeleton} paragraph={false} title={false} />
        </div>
      </div>
    </div>
  )
}

const DocumentSkeleton = () => {
  return (
    <>
      <Skeleton paragraph={{ rows: 3, className: Styles.skeletonP }} title={{ className: Styles.skeletonTitle }} />
      <Skeleton paragraph={{ rows: 3, className: Styles.skeletonPLast }} title={false} />
    </>
  )
}
